import { Accessor, BorderX, ColorX, FIconX, GFunc, LocaleX, ZArray, ZMath, ZValidate } from "IZOX/STATIC";
import { ActivityIndicator, HStack, Spacer, VStack, XButton, XDiv, XIconButton, XImage, XInput, XScroll, XText } from "IZOX/_X";
import _ from "lodash";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import AtthExpress from "IZOX/ARCS/API/AtthExpress";
import { DOMAIN, SITEBASE } from "__SYSDefault/Domain";
import ReqConfig from "__SYSDefault/_ReqConfig";
import { FiUploadCloud } from "react-icons/fi";
import reactImageSize from "react-image-size";
import { IoMdClose } from "react-icons/io";
import { FaUpload } from "react-icons/fa";
import PreviewIcon from "./_parts/PreviewIcon";

/**
 * @typedef {import("IZOX/_X/XInput").xinputparams} xinputparams
 * @typedef {import("IZOX/_X/XInput").xinputstyleparams} xinputstyleparams
 * @typedef {import("IZOX/_X/XDiv").xdivparams} xdivparams
 * @typedef {import("IZOX/STATIC/ZValidate").fvalidate} fvalidate
 * @typedef {import("IZOX/__Typedef/common").lang} lang
 * 
 * @typedef {Object} qfdragdropparam
 * @property {String} name
 * @property {lang} label
 * @property {any} defaultValue
 * @property {Function} _onChange
 * @property {Function} _onSetDefault
 * @property {any} formValue
 * @property {String} color
 * @property {String} bColor
 * @property {String} btnColor
 * @property {String} btnBgColor
 * @property {String} previewColor
 * @property {String} errorColor
 * @property {Boolean} preview
 * @property {Boolean} instantUpload
 * @property {Boolean} multiple
 * @property {Boolean} dropzone
 * @property {Boolean} button
 * @property {({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor}) => JSX.Element} renderUpload
 * @property {({value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}) => JSX.Element} renderPreview
 * @property {Boolean} readOnly
 * @property {"V" | "H"} stackDirection
 * @property {String} remarks
 * @property {Number} fileSizeLimit
 * @property {Number} fileCountLimit
 * @property {xdivparams} uploadZoneProps
 * @property {xdivparams} uploadZoneInnerProps
 * @property {xdivparams} previewZoneProps
 * @property {Boolean} noFileCaption
 * @property {Boolean} canDelete
 * @property {Boolean} keepDeleted
 * @property {Boolean} bigIcon
 * @property {({doc, field, addOns}) => Boolean} isError
 * @property {fvalidate | fvalidate[]} validators
 * @property {Object} addOns
 * @property {String | String[]} accept
 * @property {Boolean} acceptImage //mobile only
 * @property {Boolean} acceptCamera //mobile only
 * @property {Boolean} acceptFile //mobile only
 * 
 * @typedef {Object} fileItem
 * @property {String} originalName
 * @property {Number} size
 * @property {import("IZOX/STATIC/MimeTypeX").mimeType} type
 * @property {String} refID
 * @property {Number} complete
 * @property {File} oriFile 
 * 
 * @typedef {qfdragdropparam & xinputparams & xinputstyleparams} qffilepararms
 * 
 * @param {qffilepararms} param0
 * @returns 
 */
function QFFile({
  name = undefined,
  label = undefined,
  _onChange = null,
  _onSetDefault = null,
  defaultValue = undefined,
  formValue = {},
  color = ColorX.CSS("blue"),
  bColor = ColorX.CSS("lightGrey"),
  btnColor = ColorX.CSS("pureWhite"),
  btnBgColor = ColorX.CSS("blue"),
  previewColor = ColorX.CSS("blue"),
  errorColor = ColorX.CSS("bgRed", 0.5),
  preview = true,
  instantUpload = false,
  multiple = false,
  dropzone = true,
  button = true,
  renderUpload = undefined,
  renderPreview = undefined,
  readOnly = false,
  stackDirection = "H",
  remarks = "",
  accept = undefined,
  fileSizeLimit = 150,
  fileCountLimit = 20,
  uploadZoneProps = {},
  uploadZoneInnerProps = {},
  previewZoneProps = {},
  noFileCaption = false,
  canDelete = true,
  keepDeleted = true,
  updateVia = undefined, 
  updateField = undefined,
  forceUpdateField = undefined,
  performanceBoost = undefined,
  bigIcon = true,
  isError = undefined,
  addOns = undefined,
  acceptImage = undefined,
  acceptCamera = undefined,
  acceptFile = undefined,
  validators = [],
  ...others
}){
  
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [value, setValue] = useState(defaultValue ?? []);
  const [dragIn, setDragIn] = useState(false);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const GetName = useCallback(() => {
    if(_.isUndefined(name)) return name;
    return instantUpload ? name : `${name}.${ReqConfig.AttachmentFieldKey}`;
  }, [name, instantUpload]);

  useEffect(() => {
    if(defaultValue) {
      _onSetDefault?.(GetName(), defaultValue);
      setValue(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  useEffect(() => {
    let _value = Accessor.Get(formValue, GetName());
    setValue(_value);
  }, [formValue, GetName]);

  useEffect(() => {
    if(!dropzone) return;

    const dragOver = (e) => {
      e.preventDefault();
    };
    
    const dragLeave = (e) => {
      e.preventDefault();
    };

    document.addEventListener("dragover", dragOver);
    document.addEventListener("dragleave", dragLeave);

    return () => {
      document.removeEventListener("dragover", dragOver);
      document.removeEventListener("dragleave", dragLeave);
    };

  }, [dropzone]);

  if(_.isUndefined(GetName())) return null;

  const onUploadProgress = (e, idx) => {
    let {loaded, total} = e;
    let newValue = value ?? [];
    newValue[idx] = {...value?.[idx], complete: Math.round(loaded / total * 10000) /100};
    _onChange?.(GetName(), newValue);
    forceUpdate();
  };

  const uploadToBlob = async (curIdx, files) => {
    await Promise.all(_.map(files, async (o, i) => {
      let res = await AtthExpress.Upload(o, (e) => onUploadProgress(e, i), remarks);
      let {Success, payload} = res;
      if(Success){
        if(payload){
          let newValue = value ?? [];
          newValue[curIdx + i] = {...value?.[curIdx+ i], ...payload[0], complete: 100};
          _onChange?.(GetName(), newValue);
          forceUpdate();
        }
      }
    }));
  };

  const onFileUpload = async (files) => {
    if(!files) return;
    if(!multiple) files = [files[0]];
    if(!multiple && value?.length >= 1){
      await DeleteIdx(0);
    };
    //check count limit
    if(fileCountLimit && (value?.length ?? 0) + files.length > fileCountLimit){
      GFunc.Pop(
        {
          EN: "File count exceeds limit",
          TC: "檔案數量超出限制",
        },
        {
          EN: `You can only upload up to ${fileCountLimit} files.`,
          TC: `你只能上傳最多 ${fileCountLimit} 個檔案。`,
        }
      );
      return;
    }

    //check size limit
    let _limit = fileSizeLimit * 1000 * 1000;
    if(_.some(files, o => o?.size > _limit)){
      GFunc.Pop(
        {
          EN: "File size too big",
          TC: "檔案太大",
        }, 
        {
          EN: "File size exceeds: " + ZMath.FileSizeUnit(_limit).join(" "),
          TC: "檔案大於限制: " + ZMath.FileSizeUnit(_limit).join(" "),
        }
      );
      return;
    }
    
    let fileInfo = await Promise.all(_.map(files, async (o, i) => {
      let link = URL.createObjectURL(o);
      let dimension = null;
      if(o.type.includes("image")){
        try{
          dimension = await reactImageSize(link);
        }catch{}
      }
      return {
        oriFile: o, originalName: o.name, size: o.size, type: o.type, 
        link: link,
        dimension: dimension
      };
    }));
    let curIdx = value?.length ?? 0;
    let newValue = value ? [...value, ...fileInfo] : [...fileInfo];

    _onChange?.(GetName(), newValue);
    forceUpdate();
    
    if(instantUpload){
      await uploadToBlob(curIdx, files);
    }

    if(inputRef?.current){
      inputRef.current.value = "";
    }
  };

  const acceptFiles = (file) => {
    let {type, name} = file;
    let extension = "." + name.split(".").pop();
    let _accept;
    if(_.isString(accept)){
      _accept = accept.split(",");
    }else{
      _accept = accept;
    }
    _accept = ZArray.Ensure(_accept);
    return _.some(_accept, (o, i) => {
      if(o.includes("*")){
        return type.startsWith(o.replace("*", ""));
      }if(o.startsWith(".")){
        return o === extension;
      }else{
        return type === o;
      }
    });
  };

  const onFileInput = (e) => {
    e.preventDefault();
    onFileUpload?.(e.target.files);
  };

  const onDragOver = (e) => {
    setDragIn(true);
  };

  const onDragLeave = (e) => {
    setDragIn(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragIn(false);
    let _files = e.dataTransfer.files;
    if(!_.isEmpty(accept)){
      _files = _.filter(e.dataTransfer.files, o => acceptFiles(o));
    }
    if(_.isEmpty(_files)) return;
    onFileUpload?.(_files);
  };

  const onBtnClick = () => {
    inputRef.current?.click?.();
  };

  /**
   * 
   * @param {fileItem} file 
   * @param {Number} idx 
   */
  const onImageClick = async (file, idx) => {
    let {refID, complete, oriFile, type, blob, url} = file;
    if(!_.isEmpty(url)){
      if(type?.startsWith("image/")){
        GFunc.ViewImage(url);
      }else if(type?.includes("pdf")){
        GFunc.PreviewPDF(url);
      }else{
        window.open(url);
      }
      return;
    }

    if(blob) refID = blob;

    if(!_.isEmpty(refID) && !refID.startsWith("izox::")){
      if(refID.startsWith("http")){
        if(type?.includes("pdf")){
          GFunc.PreviewPDF(refID);
        }else if(type?.startsWith("image/")){
          GFunc.ViewImage(`${refID}`);
        }else{
          window.open(refID);
        }
      }else{
        if(type?.includes("pdf")){
          GFunc.PreviewPDF(`${SITEBASE}/${refID}`);
        }else if(type?.startsWith("image/")){
          GFunc.ViewImage(`${SITEBASE}/${refID}`);
        }else{
          window.open(refID);
        }
      }
      return;
    }

    //not izox
    if(type?.startsWith("image/")){
      if(refID || complete >= 100){
        let res = await AtthExpress.GetBlobUrl(refID);
        let {url} = res;
        if(!url?.startsWith("http")){
          url = `${DOMAIN}${url?.slice(1)}`;
        }
        GFunc.ViewImage(url);
      }else{
        if(oriFile){
          let url = URL.createObjectURL(oriFile);
          GFunc.ViewImage(url);
        }
      }
      return;
    }

    if(type?.includes("pdf")){
      let url = URL.createObjectURL(oriFile);
      GFunc.PreviewPDF(url);
      return;
    }

    if(refID || complete >= 100){
      let res = await AtthExpress.GetBlobUrl(refID);
      window.open(res.url);
    }else{
      if(oriFile){
        let url = URL.createObjectURL(oriFile);
        window.open(url);
      }
    }    
    
  };

  /**
   * 
   * @param {fileIt9em} file 
   * @param {Number} idx 
   */
  const onUploadClick = async (file, idx) => {

  };

  /**
   * 
   * @param {Number} idx 
   */
  const DeleteIdx = async (idx) => {
    let file = value?.[idx];
    let {refID} = file;
    if(instantUpload && !keepDeleted){
      await AtthExpress.Delete(refID);
    }

    let newValue = ZArray.DeleteIdx(value, idx);
    _onChange?.(GetName(), newValue);
    forceUpdate();
  };

  function _renderUpload({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor}){
    if(readOnly) return;
    return (
      <VStack width={200} {...uploadZoneProps}>
        <VStack forwardRef={ref} fullWidth mV={8} fullHeight VAlign="-" position="relative"
          onDragOver={dropzone ? onDragOver : undefined} onDragLeave={dropzone ? onDragLeave : undefined}
          style={{
            backgroundImage: dragIn ?
            BorderX.Render({color: bColor, dashArray: [1]})
              : BorderX.Render({color: bColor, dashArray: [18, 12]})
          }} {...uploadZoneInnerProps}>
          <VStack p={8} spacing={8} fullWidth onDrop={dropzone ? onDrop : undefined}>
            {renderDropZone({dropzone, color, dragIn})}
            {renderButton({btnColor, btnBgColor, onBtnClick})}
          </VStack>
        </VStack>
      </VStack>
    );
  }

  function renderDropZone({dropzone, color, dragIn}){
    if(!dropzone) return;
    return (
      <HStack spacing={10}>
        <FiUploadCloud color={color} size={30} style={{ transition: "color 500ms" }} />
        <XText color={color} colorTransition>
          {LocaleX.Parse(dragIn?
            {
              EN: "Relase to upload",
              TC: "放手上傳文件"
            }
            : 
            {
              EN: "Drag file to upload",
              TC: "拖曳文件上傳"
            })}
        </XText>
      </HStack>
    );
  }

  function renderButton({btnColor, btnBgColor, onBtnClick}){
    if(!button) return;
    return (
      <XButton color={btnColor} bgColor={btnBgColor} padding={0}
        onPress={onBtnClick} colorTransition
        round={50} pV={5} pH={20} width={150}>
        <HStack>
          <XText bold colorTransition>
            {LocaleX.Parse({
              EN: "Choose File",
              TC: "選擇文件"
            })}
          </XText>
        </HStack>
      </XButton>
    );
  }

  function _renderPreview({value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}){
    if(!preview || _.isEmpty(value)) return;
    return (
      <VStack flexGrow={1} fullWidth={stackDirection === "V"} fullHeight {...previewZoneProps}>
        {!noFileCaption && <HStack>
          <XText bold fullWidth textAlign="[" colorTransition 
            color={previewColor} fontSize={12} fontFamily="Arial">
            {LocaleX.Parse({
              EN: `File (${value?.length})`,
              TC: `檔案 (${value?.length})`
            })}
          </XText>
        </HStack>}
        {(bigIcon || readOnly) ?
          <XScroll spacing={8} fullWidth fullHeight>
            <HStack fitWidthToContent HAlign="[">
              {_.map(value, (o, i) => {
                return renderFileBigIcon({file: o, idx: i, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick});
              })}
            </HStack>
          </XScroll>
          :
          <XScroll spacing={8} fullWidth fullHeight>
          {_.map(value, (o, i) => {
            return renderFile({file: o, idx: i, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick});
          })}
          </XScroll>
        }
      </VStack>
    );
  }

  /**
   * 
   * @param {fileItem} file 
   * @param {Number} idx 
   */
  function renderFileBigIcon({file, idx, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}){
    if(!file) return;
    let {complete, refID} = file;
    let isCompleted = refID || complete >= 100;
    let showDelete = (isCompleted || !instantUpload) && !readOnly;
    return (
      <XDiv fitWidthToContent m={2} lockHeight={80} lockWidth={80} key={idx}>
        <PreviewIcon file={file} key={idx} iconSize={76} color={color} bColor={bColor}
          onPreview={() => onImageClick(file, idx)}
          onDelete={() => onDeleteClick(file, idx)}
          showDelete={showDelete}/>
      </XDiv>
    );
  }

  /**
   * 
   * @param {fileItem} file 
   * @param {Number} idx 
   */
  function renderFile({file, idx, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}){
    if(!file) return;
    let {originalName, size, complete, refID, type, link, blob} = file;
    let isCompleted = refID || complete >= 100;
    let [temp, unit] = ZMath.FileSizeUnit(size);
    temp = _.isNaN(temp) ? 0 : temp;

    let icon = type?.includes("image")? 
      <XImage source={link ?? blob} width={30} height={30} noResize/> 
      : FIconX.Get(type, 30, {color: color});

    return (
      <HStack key={idx} spacing={5} colorTransition
        bgColor={readOnly && idx%2 === 0 ? ColorX.CSS("aliceblue") : undefined}>
        <XDiv minWidth={30} minHeight={30} width={30} height={30}
          bW={1} bColor={ColorX.CSS("greyOut")}>
          <XButton padding={0} onPress={() => onImageClick(file, idx)} 
            tooltip={LocaleX.Parse({EN: "Preview", TC: "預覽"})}>
            {icon}
          </XButton>
        </XDiv>
        <HStack HAlign="[" flexGrow={1}>
          <VStack HAlign="[" flexGrow={1}>
            <XText color={color} fullWidth fontSize={12} colorTransition 
              noWrap ellipsisMode="ellipsis">
              {isCompleted && originalName ? originalName : LocaleX.Parse({EN: "Uploading...", TC: "上載中..."})}
            </XText>
            <HStack width={"auto"} spacing={5}>
              <XText color={bColor} fontSize={12}>
                {String(ZMath.Round(temp, 2))}
              </XText>
              <XText color={bColor} fontSize={12}>
                {unit}
              </XText>
            </HStack>
          </VStack>
        </HStack>
        <Spacer/>
        <HStack HAlign="]" width={150}>
          {!readOnly && instantUpload
            && <HStack width={30}>
              <ActivityIndicator loading={!isCompleted} size={16}/>
            </HStack>}
          {!isCompleted && instantUpload &&
            <XIconButton padding={5} onPress={() => onUploadClick(file, idx)}
              tooltip={LocaleX.Parse({EN: `${complete}%`, TC: `${complete}%`})}>
             <FaUpload color={ColorX.CSS("green")}/>
            </XIconButton>}
          {(isCompleted || !instantUpload) && !readOnly && 
            <XIconButton padding={5} onPress={() => onDeleteClick(file, idx)}  
              tooltip={LocaleX.Parse({EN: "Delete", TC: "刪除"})}>
              <IoMdClose color={ColorX.CSS("pureRed")}/>
            </XIconButton>}
        </HStack>
      </HStack>
    );
  }

  let _value = Accessor.Get(formValue, GetName());
  let _error = false;
  if(isError){
    _error = _error || isError({doc: formValue, field: _value, addOns});
  }
  if(!_.isEmpty(validators)){
    _error = _error || !ZValidate.Check(_value, validators);
  }

  /**
   * 
   * @param {fileItem} file 
   * @param {Number} idx 
   */
  const onDeleteClick = async (file, idx) => {
    if(!canDelete) return;
    let {refID} = file;
    if(instantUpload && !keepDeleted){
      await AtthExpress.Delete(refID);
    }

    let newValue = ZArray.DeleteIdx(value ?? _value, idx);
    _onChange?.(GetName(), newValue);
    forceUpdate();
  };

  switch(stackDirection){
    default: case "V":
      return (
        <VStack fullWidth pH={5} {...others} colorTransition key={_value?.length}
          bgColor={_error ? errorColor : undefined}>
          <XInput type="file" noDisplay forwardRef={inputRef}
                onChange={e => onFileInput?.(e)} accept={accept} multiple={multiple}/>
          {renderUpload?.({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor})
           ?? _renderUpload({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor})}
          {renderPreview?.({value: value ?? _value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}) 
            ?? _renderPreview({value: value ?? _value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick})}
        </VStack>
      );
    case "H":
      return (
        <HStack fullWidth bgColor={_error ? errorColor : undefined} colorTransition
          pH={5} {...others} HAlign="[" VAlign="^" spacing={10} key={_value?.length}>
          <XInput type="file" noDisplay forwardRef={inputRef}
              onChange={e => onFileInput?.(e)} accept={accept} multiple={multiple}/>
          {renderUpload?.({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor})
           ?? _renderUpload({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor})}
          {renderPreview?.({value: value ?? _value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}) 
            ?? _renderPreview({value: value ?? _value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick})}
        </HStack>
      );
  }

}

export default QFFile;
